import { Control, Controller, FieldPath } from "react-hook-form";
import { useTranslation } from "react-i18next";
import {
  Box,
  FormControl,
  FormHelperText,
  FormLabel,
  Typography,
  TableBody,
  TableRow,
  TableCell,
  Table,
  TableHead,
  Rating,
  TextField,
} from "@mui/material";
import { AddProfessionalReviewCandidate, AddTalentReviewCandidate } from "../../types/addReviewCandidate";

const RatingTable = <T extends AddProfessionalReviewCandidate | AddTalentReviewCandidate>({
  ratings,
  control,
}: {
  ratings: {
    title: string;
    description: string;
    fieldName: keyof T;
  }[];
  control: Control<T>;
}) => {
  const { t } = useTranslation("goHire", { keyPrefix: "components.reviewCandidateModal" });
  const { t: tErrors } = useTranslation("goHire", { keyPrefix: "components.reviewCandidateModal.validations" });

  const tableColumns = [
    { label: t("criteria"), value: "criteria" },
    { label: t("rating"), value: "rating" },
    { label: t("notes"), value: "notes" },
  ];

  return (
    <Box sx={styles.tableWrapper}>
      <Table sx={styles.tableContainer}>
        <TableHead>
          <TableRow>
            {tableColumns.map(({ label, value }) => (
              <TableCell key={value}>{label}</TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {ratings.map(({ title, description, fieldName }, idx) => {
            return (
              <Controller
                key={idx}
                name={fieldName as FieldPath<T>}
                control={control}
                render={({ field, fieldState: { error } }) => (
                  <TableRow>
                    <TableCell sx={{ width: "220px" }}>
                      <Box sx={styles.tableRowTitleCellContainer}>
                        <Typography sx={styles.tableRowTitleCell(!!error)}>{title}</Typography>
                        <Typography sx={styles.tableRowDescriptionCell(!!error)}>{description}</Typography>
                      </Box>
                    </TableCell>
                    <TableCell sx={styles.ratingCell}>
                      <Rating
                        size="small"
                        name={`${fieldName as string}.rating`}
                        value={(field.value as any)?.rating || 0}
                        onChange={(event, newValue) => {
                          field.onChange({
                            ...((field.value as any) || {}),
                            rating: newValue || 0,
                          });
                        }}
                      />
                      {error && (
                        // @ts-ignore
                        <FormHelperText error>{tErrors(error?.rating?.message || error?.message)}</FormHelperText>
                      )}
                    </TableCell>
                    <TableCell>
                      <TextField
                        fullWidth
                        placeholder={t("writeYourNotesHere")}
                        multiline
                        rows={3}
                        sx={styles.notesField}
                        value={(field.value as any)?.notes || ""}
                        onChange={(event) => {
                          field.onChange({
                            ...((field.value as any) || {}),
                            notes: event.target.value,
                          });
                        }}
                      />
                    </TableCell>
                  </TableRow>
                )}
              />
            );
          })}
        </TableBody>
      </Table>
    </Box>
  );
};

export default RatingTable;

const styles = {
  tableWrapper: {
    border: "1px solid var(--Gray-200, #EAECF0)",
    borderRadius: "12px",
    maxHeight: "60vh",
    overflowY: "auto",
  },
  tableContainer: {
    "& .MuiTableHead-root": {
      backgroundColor: "var(--Gray-50, #F9FAFB)",
    },
    "& .MuiTableCell-head": {
      color: "var(--Gray-600, #475467)",
      fontFamily: "Montserrat",
      fontSize: "12px",
      fontStyle: "normal",
      fontWeight: 600,
      lineHeight: "18px",
      padding: "12px 20px",
      textWrap: "nowrap",
      borderRight: "1px solid var(--Gray-200, #EAECF0)",
      borderBottom: "1px solid var(--Gray-200, #EAECF0)",
    },
    "& .MuiTableCell-head:last-of-type": {
      borderRight: "none",
    },
    "& .MuiTableCell-root": {
      textWrap: "nowrap",
    },
    "& .MuiTableCell-body": {
      borderBottom: "1px solid var(--Gray-200, #EAECF0)",
      borderRight: "1px solid var(--Gray-200, #EAECF0)",
      padding: "16px",
    },
    "& .MuiTableCell-body:last-of-type": {
      borderRight: "none",
    },
    "& .MuiTableRow-root:last-child .MuiTableCell-body": {
      borderBottom: "none",
    },
  },
  ratingCell: {
    verticalAlign: "top",
  },
  tableRowTitleCellContainer: {
    display: "flex",
    flexDirection: "column",
    gap: "4px",
    width: "220px",
  },
  tableRowTitleCell: (error: boolean) => ({
    fontFamily: "Montserrat",
    fontSize: "14px",
    fontStyle: "normal",
    fontWeight: 600,
    lineHeight: "20px",
    color: error ? "var(--Error-500, #F04438)" : "var(--Gray-500, #667085)",
  }),
  tableRowDescriptionCell: (error: boolean) => ({
    fontFamily: "Montserrat",
    fontSize: "14px",
    fontStyle: "normal",
    fontWeight: 400,
    lineHeight: "20px",
    textWrap: "wrap",
    color: error ? "var(--Error-500, #F04438)" : "var(--Gray-500, #667085)",
  }),
  notesField: {
    color: "var(--Gray-500, #667085)",
    fontFamily: "Montserrat",
    fontSize: "16px",
    fontStyle: "normal",
    fontWeight: 400,
    lineHeight: "24px",
    "& .MuiInputBase-input": {
      padding: "8px 0",
    },
  },
};
